/**
 * A1 (round 8 persona audit) — a single app-level `aria-live="polite"` region for
 * every /interlock screen, so toast text and bell-count changes get announced to
 * screen readers instead of only ever being conveyed visually. Same
 * publish/subscribe shape as `utils/interlockActor.ts`'s `subscribeActor`, kept
 * intentionally tiny: one message at a time, no queue, no history.
 *
 * `<LiveRegion />` (below) is the only DOM consumer — mount it once per shell
 * (`InterlockScaffold`, and `TradeDetailPage`'s own `Shell`, since that page
 * predates the scaffold and renders its own rail + stage rather than going
 * through it). Anything else that wants to announce calls `announce(text)`.
 */
import { useEffect, useState } from 'react';

type Listener = (message: string) => void;

const listeners = new Set<Listener>();

/** Announce `message` to whichever `<LiveRegion />` is currently mounted. A
 * repeat of the exact same string as last time (e.g. two consecutive "Comment
 * added" toasts) is nudged with a trailing " ." so assistive tech that
 * dedupes identical live-region text still announces the second one. */
let lastMessage = '';

export function announce(message: string): void {
  const text = message.trim();
  if (!text) return;
  const payload = text === lastMessage ? `${text} .` : text;
  lastMessage = text;
  for (const fn of listeners) fn(payload);
}

/** Renders the visually-hidden `aria-live="polite"` node itself. Mount exactly
 * one per page shell. */
export function LiveRegion() {
  const [message, setMessage] = useState('');
  useEffect(() => {
    const fn: Listener = (m) => setMessage(m);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return (
    <div aria-live="polite" role="status" className="sr-only">
      {message}
    </div>
  );
}
